import React from 'react';
import { Flavor, Mode, ModePreference, Density, COLOR_TOKENS } from '../../theme/signatureTokens';
import { getGalateaLogoUrl, getTenantLogoUrl } from '../../utils/tenantLogo';
import { useAuth } from '../../contexts/AuthContext';
import { 
  IconLayoutDashboard, 
  IconShoppingCart, 
  IconPackage, 
  IconFileInvoice, 
  IconCashBanknote, 
  IconSparkles,
  IconCheck
} from '@tabler/icons-react';

interface AppearanceLivePreviewProps {
  draftFlavor: Flavor;
  draftMode: ModePreference;
  draftDensity: Density;
  draftLanguage: string;
  draftLocale: string;
}

export const AppearanceLivePreview: React.FC<AppearanceLivePreviewProps> = ({
  draftFlavor,
  draftMode,
  draftDensity,
  draftLanguage,
  draftLocale,
}) => {
  const { getUserContext, getEmpresaId, getEmpresaNome, getEmpresaCnpj } = useAuth();
  
  const userContext = getUserContext();
  const empresaId = getEmpresaId();
  const empresaNome = getEmpresaNome() || 'Attuante';
  const empresaCnpj = getEmpresaCnpj() || '32.469.511/0001-43';
  const logoUrl = getTenantLogoUrl(userContext || { empresa_id: empresaId, empresa_nome: empresaNome });

  // Modo efetivo do preview: 'auto' resolve pela preferência do sistema operacional
  const effectiveMode: Mode =
    draftMode === 'auto'
      ? (typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light')
      : draftMode;

  const colors = COLOR_TOKENS[draftFlavor]?.[effectiveMode] || COLOR_TOKENS.SIGNATURE.light;

  // Formatação de moeda por locale
  const getCurrencySymbol = (loc: string) => {
    if (loc.includes('UY')) return '$U';
    if (loc.includes('PY')) return '₲';
    if (loc.includes('US')) return '$';
    if (loc.includes('AR') || loc.includes('CL')) return '$';
    return 'R$';
  };

  const currencySymbol = getCurrencySymbol(draftLocale);

  return (
    <div 
      className="w-full rounded-xl border overflow-hidden shadow-md transition-all duration-300 select-none"
      style={{ 
        backgroundColor: colors.bgApp, 
        borderColor: colors.border 
      }}
    >
      {/* Header Bar do Preview */}
      <div 
        className="h-11 px-3 flex items-center justify-between border-b transition-colors"
        style={{ 
          backgroundColor: colors.bgHeader, 
          borderColor: colors.divider 
        }}
      >
        <div className="flex items-center gap-3">
          {/* Logo Galatea Soberana */}
          <img src={getGalateaLogoUrl()} alt="Galatea" className="h-6 w-auto object-contain" />

          {/* Logo do Tenant (S3) */}
          <div className="flex items-center gap-2 border-l border-slate-700/60 pl-3">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt={empresaNome} 
                className="w-6 h-6 object-contain rounded bg-white p-0.5 shadow-xs" 
              />
            ) : (
              <div className="w-6 h-6 rounded bg-sky-600 text-white font-bold text-[10px] flex items-center justify-center">
                {empresaNome.charAt(0)}
              </div>
            )}
            <div className="flex flex-col">
              <span className="text-[11px] font-bold text-slate-100 leading-none truncate max-w-[120px]">
                {empresaNome}
              </span>
              <span className="text-[9px] text-slate-400 font-mono leading-none">
                {empresaCnpj}
              </span>
            </div>
          </div>
        </div>

        {/* Badges de Estado no Preview Header */}
        <div className="flex items-center gap-2">
          <span 
            className="text-[10px] font-mono px-2 py-0.5 rounded font-bold uppercase tracking-wider"
            style={{ backgroundColor: colors.primary, color: '#FFFFFF' }}
          >
            {draftFlavor} · {draftMode.toUpperCase()}
          </span>
          <div className="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center shadow-xs">
            <IconSparkles size={13} />
          </div>
        </div>
      </div>

      {/* Corpo do Preview (Sidebar + Workspace) */}
      <div className="flex h-56">
        {/* Sidebar */}
        <div 
          className="w-44 border-r p-2 flex flex-col justify-between shrink-0"
          style={{ 
            backgroundColor: colors.bgSidebar, 
            borderColor: colors.divider 
          }}
        >
          <div className="space-y-1">
            <div className="text-[9px] font-mono uppercase tracking-wider px-2 py-1 text-slate-400 font-bold">
              Menu Principal
            </div>
            
            <div 
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors"
              style={{ backgroundColor: colors.sidebarSelected, color: '#FFFFFF' }}
            >
              <IconLayoutDashboard size={14} />
              <span>Painel de Controle</span>
            </div>

            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-slate-300 hover:bg-slate-800/50">
              <IconShoppingCart size={14} />
              <span>Vendas</span>
            </div>

            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-slate-300 hover:bg-slate-800/50">
              <IconPackage size={14} />
              <span>Estoque</span>
            </div>

            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-slate-300 hover:bg-slate-800/50">
              <IconFileInvoice size={14} />
              <span>Emissão NF-e</span>
            </div>

            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-slate-300 hover:bg-slate-800/50">
              <IconCashBanknote size={14} />
              <span>Financeiro</span>
            </div>
          </div>

          <div className="text-[9px] font-mono text-slate-400 px-2 py-1">
            Build 2026.08 · Galatea Signature
          </div>
        </div>

        {/* Workspace Analítico */}
        <div 
          className="flex-1 p-3 overflow-hidden flex flex-col gap-2.5"
          style={{ backgroundColor: colors.bgWorkspace }}
        >
          {/* Subtítulo de contexto do preview */}
          <div className="flex items-center justify-between">
            <div>
              <div 
                className="text-xs font-bold leading-tight"
                style={{ color: colors.textPrimary }}
              >
                Visão Consolidada de Operações
              </div>
              <div 
                className="text-[10px]"
                style={{ color: colors.textSecondary }}
              >
                Empresa: {empresaNome} · Formato: {currencySymbol} ({draftLocale}) · {draftLanguage}
              </div>
            </div>

            <span 
              className="text-[10px] px-2 py-0.5 rounded font-mono font-medium border"
              style={{ 
                backgroundColor: colors.bgSurface, 
                color: colors.textSecondary,
                borderColor: colors.border 
              }}
            >
              Densidade: {draftDensity}
            </span>
          </div>

          {/* Cards KPIs (Arquétipo CONTROL) */}
          <div className="grid grid-cols-3 gap-2">
            <div 
              className="p-2 rounded-lg border shadow-xs"
              style={{ backgroundColor: colors.bgSurface, borderColor: colors.border }}
            >
              <div className="text-[9px] font-mono" style={{ color: colors.textSecondary }}>Faturamento Mês</div>
              <div className="text-xs font-bold mt-0.5" style={{ color: colors.textPrimary }}>
                {currencySymbol} 248.500,00
              </div>
            </div>

            <div 
              className="p-2 rounded-lg border shadow-xs"
              style={{ backgroundColor: colors.bgSurface, borderColor: colors.border }}
            >
              <div className="text-[9px] font-mono" style={{ color: colors.textSecondary }}>Pedidos Emitidos</div>
              <div className="text-xs font-bold mt-0.5" style={{ color: colors.textPrimary }}>
                142 Pedidos
              </div>
            </div>

            <div 
              className="p-2 rounded-lg border shadow-xs"
              style={{ backgroundColor: colors.bgSurface, borderColor: colors.border }}
            >
              <div className="text-[9px] font-mono" style={{ color: colors.textSecondary }}>Status Fiscal</div>
              <div className="text-xs font-bold mt-0.5 text-emerald-600 flex items-center gap-1">
                <IconCheck size={12} /> 100% Ok
              </div>
            </div>
          </div>

          {/* Tabela Resumida (Arquétipo GRID) */}
          <div 
            className="flex-1 rounded-lg border overflow-hidden"
            style={{ backgroundColor: colors.bgSurface, borderColor: colors.border }}
          >
            <div 
              className="px-2.5 py-1 border-b text-[10px] font-bold grid grid-cols-4"
              style={{ backgroundColor: colors.bgSurfaceHover, borderColor: colors.divider, color: colors.textSecondary }}
            >
              <span>PEDIDO</span>
              <span>CLIENTE</span>
              <span>VALOR</span>
              <span>STATUS</span>
            </div>

            <div className="divide-y" style={{ borderColor: colors.divider }}>
              <div className="px-2.5 py-1.5 text-[10px] grid grid-cols-4 items-center" style={{ color: colors.textPrimary }}>
                <span className="font-mono font-bold">#PED-9042</span>
                <span className="truncate">Comfitas Indústria</span>
                <span className="font-mono">{currencySymbol} 14.800,00</span>
                <span className="text-[9px] font-bold text-emerald-600">Aprovado</span>
              </div>

              <div className="px-2.5 py-1.5 text-[10px] grid grid-cols-4 items-center" style={{ color: colors.textPrimary }}>
                <span className="font-mono font-bold">#PED-9043</span>
                <span className="truncate">Ecotech Brasil</span>
                <span className="font-mono">{currencySymbol} 8.250,00</span>
                <span className="text-[9px] font-bold text-sky-600">Em Trânsito</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
